// ---------------------------------------------------------------------------
// Wissen beim Schreiben (PLAN.md §16b): Zwei Arten von Bezug halten fest, wer an welcher Stelle etwas erfährt –
// `[Stelle]{@clara @brief erfährt}`: eine Figur erfährt etwas; `[Stelle]{@brief enthüllt}`: der Leser erfährt es.
// Personen im Bezug sind die, die etwas erfahren; alle anderen Elemente (meist ein Konzept) sind, was sie erfahren. Ohne Person erfährt
// es die Perspektivfigur der Szene. Daraus entsteht das Wissen ohne eigenes Formular: wer was seit welcher Szene weiß – Figuren in
// Weltzeit, der Leser in Lesereihenfolge.
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { draftOf, narrativeOrder, povOf, worldOrder } from './narrative.js';
import { plainRefs, refIndex } from './refs.js';
import { isScene } from './scene.js';
import { priorLabel } from './series.js';
import { spansIn, stripSpans } from './spans.js';
import type { PriorSource, StoryNode } from './types.js';

/** Die Bezugsarten des Wissens: eine Figur erfährt etwas, der Leser erfährt es (es wird enthüllt) */
export const KNOW_ROLES = ['erfährt', 'enthüllt'] as const;
export type KnowRole = (typeof KNOW_ROLES)[number];
export const isKnowRole = (role: string | null): role is KnowRole => role === 'erfährt' || role === 'enthüllt';

/** Kann etwas erfahren: Einträge der vordefinierten Klasse Person */
export const isKnower = (ctx: StoryCtx, entityId: string): boolean => {
  const e = ctx.entities.get(entityId);
  const t = e ? ctx.types.get(e.typeId) : undefined;
  return !!t && t.builtin && t.name === 'Person';
};

/** Eine Stelle, an der jemand etwas erfährt */
export interface Learning {
  sceneId: string;
  docId: string;
  role: KnowRole;
  /** wer es erfährt (Figuren); beim Enthüllen leer – es erfährt der Leser */
  knowers: string[];
  /** was (Einträge); leer: die Stelle selbst ist das Wissen */
  about: string[];
  /** die Stelle als Klartext */
  text: string;
}

/**
 * Die Stellen einer Szene, an denen jemand etwas erfährt. „Erfährt“ zählt in allen Texten der Szene (auch in Notizen beim Plotten),
 * „enthüllt“ nur im Draft – der Leser liest nur ihn.
 */
export function learningsIn(ctx: StoryCtx, scene: StoryNode): Learning[] {
  const refs = refIndex(ctx);
  const draft = draftOf(scene);
  const pov = povOf(ctx, scene);
  const out: Learning[] = [];
  for (const doc of scene.documents) {
    for (const s of spansIn(doc.text)) {
      if (!isKnowRole(s.role)) continue;
      if (s.role === 'enthüllt' && doc.text !== draft) continue;
      const ids = [...new Set(s.keys.map((k) => refs.get(k)?.id).filter((id): id is string => !!id))];
      const text = plainRefs(stripSpans(doc.text.slice(s.textFrom, s.textTo))).replace(/\s+/g, ' ').trim();
      if (s.role === 'enthüllt') {
        out.push({ sceneId: scene.id, docId: doc.id, role: s.role, knowers: [], about: ids, text });
        continue;
      }
      const persons = ids.filter((id) => isKnower(ctx, id));
      const knowers = persons.length > 0 ? persons : pov ? [pov] : [];
      out.push({ sceneId: scene.id, docId: doc.id, role: s.role, knowers, about: ids.filter((id) => !persons.includes(id)), text });
    }
  }
  return out;
}

/** Ein Wissen: wer (Figur bzw. null = der Leser) weiß was (Eintrag bzw. null = die Stelle) seit welcher Szene */
export interface Known {
  /** Figur; null: der Leser */
  knower: string | null;
  /** Eintrag; null: ohne Element (die Stelle selbst) */
  about: string | null;
  text: string;
  /** die erste Szene, in der es erfahren wird (Weltzeit bzw. beim Leser Lesereihenfolge) */
  sceneId: string;
  docId: string;
  /** „Kapitel.Szene“ der Szene, leer wenn nicht eingeordnet */
  label: string;
  /** Stelle der Szene in Weltzeit (Szenen ohne Zeit danach) bzw. in der Lesereihenfolge (null: nicht eingeordnet) */
  world: number;
  reading: number | null;
  /** wie oft es (später) noch erfahren wird */
  again: number;
  /** schon aus einer früheren Episode der Serie (series.ts): ohne Szene hier (`sceneId` leer, Weltzeit und Lesestelle -1) */
  prior?: PriorSource;
}

/**
 * Was jemand weiß bzw. wer etwas weiß – je Paar (wer, was) nur das erste Erfahren: für Figuren in Weltzeit (Szenen ohne Zeit danach),
 * für den Leser in Lesereihenfolge (nur eingeordnete Szenen).
 */
export function knowledgeOf(ctx: StoryCtx): Known[] {
  const order = narrativeOrder(ctx);
  const labels = new Map(order.scenes.map((e) => [e.scene.id, e.label]));
  const reading = order.scenes.map((e) => e.scene);
  const readingIndex = new Map(order.scenes.map((e) => [e.scene.id, e.index]));
  const world = worldOrder(ctx, ctx.story.nodes.filter(isScene));
  const worldIndex = new Map(world.map((s, i) => [s.id, i]));
  const out = new Map<string, Known>();
  // was schon aus früheren Episoden gewusst wird, ist das erste Erfahren
  for (const p of ctx.story.series?.knowledge ?? []) {
    if (!ctx.entities.has(p.about) || (p.knower !== null && !ctx.entities.has(p.knower))) continue;
    const k = `${p.knower ?? ''}\n${p.about}`;
    if (!out.has(k))
      out.set(k, { knower: p.knower, about: p.about, text: p.text, sceneId: '', docId: '', label: priorLabel(p.episode), world: -1, reading: -1, again: 0, prior: { episode: p.episode, scene: p.scene } });
  }
  const add = (knower: string | null, about: string | null, l: Learning): void => {
    // ohne Element ist jede Stelle ein eigenes Wissen
    const k = `${knower ?? ''}\n${about ?? `${l.sceneId}:${l.text}`}`;
    const had = out.get(k);
    if (had) had.again += 1;
    else
      out.set(k, {
        knower,
        about,
        text: l.text,
        sceneId: l.sceneId,
        docId: l.docId,
        label: labels.get(l.sceneId) ?? '',
        world: worldIndex.get(l.sceneId)!,
        reading: readingIndex.get(l.sceneId) ?? null,
        again: 0,
      });
  };
  for (const scene of world)
    for (const l of learningsIn(ctx, scene)) {
      if (l.role !== 'erfährt') continue;
      for (const w of l.knowers) for (const a of l.about.length > 0 ? l.about : [null]) add(w, a, l);
    }
  for (const scene of reading)
    for (const l of learningsIn(ctx, scene)) {
      if (l.role !== 'enthüllt') continue;
      for (const a of l.about.length > 0 ? l.about : [null]) add(null, a, l);
    }
  return [...out.values()];
}

/** Wissen rund um ein Element: was es (als Figur) weiß, wer es (als Gegenstand des Wissens) weiß und seit wann der Leser */
export interface EntityKnowledge {
  knows: Known[];
  knownBy: Known[];
  reader: Known | null;
}

export function entityKnowledge(ctx: StoryCtx, entityId: string, all: Known[] = knowledgeOf(ctx)): EntityKnowledge {
  return {
    knows: all.filter((k) => k.knower === entityId),
    knownBy: all.filter((k) => k.about === entityId && k.knower !== null),
    reader: all.find((k) => k.about === entityId && k.knower === null) ?? null,
  };
}

/** Wo eine Szene im Verhältnis zu dem Wissen steht: vorher erfahren (weiß es), hier, erst später; null: nicht bestimmbar */
export type KnowWhen = 'before' | 'here' | 'later';
export type KnownAt = Known & { when: KnowWhen | null };

/** Wissen rund um ein Element zur Zeit einer Szene (Element-Kontext beim Schreiben) */
export interface KnowledgeAt {
  /** als Figur: was sie bis hier weiß, hier erfährt bzw. erst später erfährt (Weltzeit) */
  knows: KnownAt[];
  /** als Gewusstes: wer es bis hier weiß, hier bzw. später erfährt (Weltzeit) */
  knownBy: KnownAt[];
  /** der Leser (Lesereihenfolge); null: wird nie enthüllt */
  reader: KnownAt | null;
}

/**
 * Wissen zur Zeit der Szene `sceneId`: Figuren nach Weltzeit (die Szene, in der sie es zuerst erfahren, vor, gleich oder nach dieser), der
 * Leser nach Lesereihenfolge (in einer nicht eingeordneten Szene nicht bestimmbar).
 */
export function knowledgeAt(ctx: StoryCtx, entityId: string, sceneId: string, all: Known[] = knowledgeOf(ctx)): KnowledgeAt {
  const world = worldOrder(ctx, ctx.story.nodes.filter(isScene)).findIndex((s) => s.id === sceneId);
  const reading = narrativeOrder(ctx).scenes.find((e) => e.scene.id === sceneId)?.index ?? null;
  const at = (k: Known): KnownAt => ({ ...k, when: k.sceneId === sceneId ? 'here' : k.world < world ? 'before' : 'later' });
  const k = entityKnowledge(ctx, entityId, all);
  const r = k.reader;
  return {
    knows: k.knows.map(at),
    knownBy: k.knownBy.map(at),
    reader: r ? { ...r, when: r.sceneId === sceneId ? 'here' : reading === null || r.reading === null ? null : r.reading < reading ? 'before' : 'later' } : null,
  };
}

/**
 * Spannung zwischen Figur und Leser in der Lesereihenfolge: `irony` – der Leser weiß es, bevor die Figur es erfährt (dramatische Ironie);
 * `mystery` – die Figur weiß es, bevor (bzw. ohne dass) der Leser es erfährt (Rätsel).
 */
export type KnowTension = 'irony' | 'mystery';

export interface KnowledgeRow {
  /** das Gewusste (Eintrag) */
  about: string;
  /** je Figur der Übersicht: seit wann sie es weiß, samt Spannung zum Leser */
  cells: ((Known & { tension: KnowTension | null }) | null)[];
  reader: Known | null;
  /** Figuren wissen es, dem Leser wird es nie enthüllt */
  secret: boolean;
}

export interface KnowledgeMatrix {
  /** Figuren, die etwas erfahren – in der Reihenfolge ihres ersten Erfahrens beim Lesen */
  knowers: string[];
  rows: KnowledgeRow[];
}

/** Wissensübersicht: Gewusstes (nur Einträge) mal Figuren und Leser, je mit der ersten Szene und der Spannung zwischen Figur und Leser. */
export function knowledgeMatrix(ctx: StoryCtx, all: Known[] = knowledgeOf(ctx)): KnowledgeMatrix {
  const items = all.filter((k): k is Known & { about: string } => k.about !== null);
  // Reihenfolge: wie man beim Lesen darauf trifft (nicht Eingeordnetes danach, in Weltzeit)
  const pos = (k: Known): number => (k.reading ?? 1e6) * 1e6 + k.world;
  const first = new Map<string, number>();
  const firstKnower = new Map<string, number>();
  for (const k of items) {
    first.set(k.about, Math.min(first.get(k.about) ?? Infinity, pos(k)));
    if (k.knower) firstKnower.set(k.knower, Math.min(firstKnower.get(k.knower) ?? Infinity, pos(k)));
  }
  const knowers = [...firstKnower.keys()].sort((a, b) => firstKnower.get(a)! - firstKnower.get(b)!);
  const rows = [...first.keys()]
    .sort((a, b) => first.get(a)! - first.get(b)!)
    .map((about): KnowledgeRow => {
      const reader = items.find((k) => k.about === about && k.knower === null) ?? null;
      const cells = knowers.map((w) => {
        const k = items.find((x) => x.about === about && x.knower === w);
        if (!k) return null;
        const tension: KnowTension | null =
          k.reading === null ? null : reader?.reading != null && reader.reading < k.reading ? 'irony' : !reader || (reader.reading !== null && k.reading < reader.reading) ? 'mystery' : null;
        return { ...k, tension };
      });
      return { about, cells, reader, secret: !reader && cells.some((c) => c !== null) };
    });
  return { knowers, rows };
}
